import { NgTemplateOutlet, JsonPipe, AsyncPipe } from '@angular/common';
import {
  SimpleChanges,
  TemplateRef,
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  output,
  input,
} from '@angular/core';

import type { Observable } from 'rxjs';

import { TranslatePipe } from '@core/i18n-legacy';
import {
  ReportDashboardComponentViewData,
  ReportDashboardMetricOptions,
  VisualizationDataTypeUnion,
  DetailsDimensionsData,
  injectReportsChartTheme,
  VisualizationType,
  ReportDashboardComponentViewMode,
  SummaryDataItem,
} from '@core/reports-types';
import { getComponentVisualizationType, buildDetailsDimensionsData } from '@core/reports-util';
import type { SortDirection, SortValue } from '@core/search-types';
import type { Theme } from '@core/theme-util';
import type { DataTableColumn } from '@core/user-interface-legacy';
import { EmptyStateComponent } from '@core/user-interface-legacy';

import {
  PresentionalFailedEmptyStateReason,
  ReportFailedEmptyStateComponent,
} from '../failed-empty-state/failed-empty-state.component';
import { ReportsLegacyChartComponent } from '../legacy-chart/legacy-chart.component';
import { ReportDashboardsPivotTableComponent } from '../pivot-table/pivot-table.component';
import { DashboardsSummaryComponent } from '../summary/summary.component';
import { ReportDashboardsTableComponent } from '../table/table.component';

@Component({
  selector: 'reports-visualization-renderer',
  templateUrl: './visualization-renderer.component.html',
  styleUrls: ['./visualization-renderer.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    DashboardsSummaryComponent,
    ReportDashboardsTableComponent,
    ReportsLegacyChartComponent,
    EmptyStateComponent,
    ReportFailedEmptyStateComponent,
    JsonPipe,
    TranslatePipe,
    AsyncPipe,
    ReportDashboardsPivotTableComponent,
  ],
})
export class VisualizationRendererComponent {
  public readonly resourcesPreviewAvailable = input<boolean>(true);
  public readonly component = input<ReportDashboardComponentViewData>(undefined!);
  public readonly metric = input<ReportDashboardMetricOptions>(undefined!);
  public readonly data = input<VisualizationDataTypeUnion>();
  public readonly loading = input<boolean>(undefined!);
  public readonly loaded = input<boolean>(undefined!);
  public readonly failed = input<boolean>(undefined!);
  public readonly fullscreenEnabled = input<boolean>(false);
  public readonly currColWidth = input<number>();
  public readonly allowRefreshing = input<boolean>(undefined!);
  public readonly export = input(false);
  public readonly paginateTable = input(true);
  public readonly visualizationDisabled = input<PresentionalFailedEmptyStateReason | null>(null);

  public tableSortChange = output<SortValue>();
  public editClick = output<void>();
  public refresh = output<void>();
  public openDetails = output<DetailsDimensionsData>();
  public rendered = output<void>();

  @ViewChild('visualization', { static: true }) public visualizationTemplate: TemplateRef<any>;
  @ViewChild('empty', { static: true }) public emptyTemplate: TemplateRef<any>;
  @ViewChild('failed', { static: true }) public failedTemplate: TemplateRef<any>;
  @ViewChild('pending', { static: true }) public pendingTemplate: TemplateRef<any>;

  public template: TemplateRef<any>;
  public theme$: Observable<Theme> = injectReportsChartTheme();
  public VisualizationType = VisualizationType;
  public alreadyLoaded = false;

  public ngAfterViewInit() {
    const component = this.component();
    if (!component || component.viewMode === ReportDashboardComponentViewMode.Chart) {
      return;
    }
    this.rendered.emit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!this.alreadyLoaded && changes && changes.loaded && changes.loaded.currentValue === true) {
      this.alreadyLoaded = true;
    }
    this.template = this.getActiveTemplate();
  }

  public getComponentVisualizationType() {
    return getComponentVisualizationType(this.component());
  }

  public openResourcesAt(target: [number, number]) {
    const detailsData = buildDetailsDimensionsData(this.data()!.dataset, target);
    this.openDetails.emit(detailsData);
  }

  public openResourcesFromSummaryItem({ dimensions }: SummaryDataItem) {
    this.openDetails.emit({
      primary: dimensions.primary!,
      secondary: dimensions.secondary,
      dataset: this.data()!.dataset,
    });
  }

  public handleSortChange({ id, sort }: DataTableColumn) {
    this.tableSortChange.emit({ columnId: id, direction: <SortDirection>sort ?? null });
  }

  private getActiveTemplate(): TemplateRef<any> {
    const isEmpty = this.data()?.dataset.isEmpty ?? false;
    const isInitial = this.data()?.dataset.isInitial ?? false;

    if (this.visualizationDisabled()) {
      return this.failedTemplate;
    }

    const failed = this.failed();
    if (this.alreadyLoaded || failed) {
      if (isEmpty && !isInitial) {
        return this.emptyTemplate;
      }

      if (failed) {
        return this.failedTemplate;
      }

      if (!isInitial) {
        return this.visualizationTemplate;
      }
    }

    return this.template;
  }
}
